import { TErrorNotification } from '@/components/ErrorInlineNotification/ErrorInlineNotification';
import { usePostApiJsApPayeesInvitationsPdfs } from '@/context/services/doc_instructions/DocInstructions.service';
import { InstructionPdfForm } from '@/features/ap_payees/components/DocInstructionMenu/types';
import { useTranslation } from '@/i18n';
import { isCommonError, isResponseError } from '@/libs/typeguard/isError';
import { isObjectEmpty } from '@/utils/isObjectEmpty';
import type {
  Error as ApiFieldError,
  UpdateReceivedInvoiceErrorResponse,
} from 'ap-openapi';
import {
  PostApiJsApPayeesInvitationsPdfsBody,
  SearchApPayeeQuery,
  SearchedApPayee,
} from 'ap-openapi';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

export const useDownloadInstructions = (
  payeeQuery?: SearchApPayeeQuery,
  payeeList?: SearchedApPayee[],
  setError?: TErrorNotification['setError']
) => {
  const formMethods = useFormContext<InstructionPdfForm>();
  const { mutateAsync, isPending } = usePostApiJsApPayeesInvitationsPdfs();
  const { t } = useTranslation();

  const handleDownload = useCallback(async () => {
    const formValues = formMethods.getValues();

    let safePayeeQuery: SearchApPayeeQuery = {};

    // First try to use the provided payeeQuery
    if (payeeQuery && !isObjectEmpty(payeeQuery)) {
      safePayeeQuery = { ...payeeQuery };
    }
    // If no valid payeeQuery, but we have payeeList, extract IDs
    else if (payeeList && payeeList.length > 0) {
      // Filter out any undefined IDs and ensure we only have strings
      const validIds = payeeList
        .map((payee) => payee.id)
        .filter((id): id is string => id !== undefined);
      safePayeeQuery = {
        ids: validIds,
      };
    }

    const payload: PostApiJsApPayeesInvitationsPdfsBody = {
      ap_payee_query: safePayeeQuery,
      content: formValues.content || '',
      destination: formValues.destination || '',
      save_as_template: Boolean(formValues.saveAsTemplate),
      sender: formValues.sender || '',
      title: formValues.title || '',
    };

    try {
      const { status, errors, redirectUrl } = await mutateAsync({
        data: payload,
      });
      if (status === 'success' && redirectUrl) {
        sessionStorage.setItem('export_files_download_progressing', 'true');
        window.location.href = redirectUrl;
        return;
      }

      if (status !== 'error' || !errors) return;

      if (isCommonError(errors)) {
        setError?.(new Error(errors?.messages?.join('\n')));
        return;
      }

      if (
        isResponseError<UpdateReceivedInvoiceErrorResponse>(errors, 'errors')
      ) {
        const fieldErrors = errors?.errors ?? {};
        const allMessages = Object.values(fieldErrors)
          .flat()
          .map((err: ApiFieldError) => err.message)
          .filter(Boolean)
          .join('\n');
        setError?.(new Error(allMessages || t('system_error')));
        return;
      }
    } catch (error) {
      if (setError) {
        if (error instanceof Error) {
          setError(error);
        } else {
          setError(new Error(t('system_error')));
        }
      }
    }
  }, [formMethods, payeeQuery, payeeList, mutateAsync, setError, t]);

  return {
    handleDownload,
    isLoading: isPending,
  };
};
